import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Persona, StudioId } from '@/domain/types';
import type { ActiveStudio } from '@/domain/studioScope';

export interface SessionState {
  persona: Persona | null;
  agentId: string | null;
  artistId: string | null;
  apprenticeId: string | null;
  assignedStudios: StudioId[];
  // The studio switcher's live selection: a specific studio or the network-wide
  // 'all'. Every studio-scoped screen reads this (via useActiveStudio) so the
  // top-bar switcher is the single source of truth for scope.
  activeStudio: ActiveStudio | null;
  activeCustomerId: string | null;
  loggedInAt: string | null;
  // Comms read-state: maps channelId or dmThreadId → last-read ISO timestamp.
  // Lives in session (not the brain) so per-persona reads don't leak into the
  // shared fixture set.
  lastReadByChannel: Record<string, string>;
  setPersona: (
    persona: Persona,
    opts?: {
      agentId?: string;
      artistId?: string;
      apprenticeId?: string;
      assignedStudios?: StudioId[];
      activeStudio?: ActiveStudio;
    },
  ) => void;
  setActiveStudio: (studio: ActiveStudio) => void;
  setActiveCustomer: (id: string | null) => void;
  markCommsRead: (id: string, iso: string) => void;
  logout: () => void;
}

const initialState = {
  persona: null,
  agentId: null,
  artistId: null,
  apprenticeId: null,
  assignedStudios: [] as StudioId[],
  activeStudio: null,
  activeCustomerId: null,
  loggedInAt: null,
  lastReadByChannel: {} as Record<string, string>,
};

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      ...initialState,
      setPersona: (persona, opts) =>
        set({
          persona,
          agentId: opts?.agentId ?? null,
          artistId: opts?.artistId ?? null,
          apprenticeId: opts?.apprenticeId ?? null,
          assignedStudios: opts?.assignedStudios ?? [],
          activeStudio: opts?.activeStudio ?? opts?.assignedStudios?.[0] ?? null,
          loggedInAt: new Date().toISOString(),
        }),
      setActiveStudio: (studio) => set({ activeStudio: studio }),
      setActiveCustomer: (id) => set({ activeCustomerId: id }),
      markCommsRead: (id, iso) =>
        set((s) => ({ lastReadByChannel: { ...s.lastReadByChannel, [id]: iso } })),
      logout: () => set(initialState),
    }),
    {
      name: 'ati-session',
      // localStorage (not sessionStorage): the logged-in persona is shared across
      // every tab on the origin, so a freshly opened tab acts as the same user
      // instead of landing unauthenticated (tv-k9b.23).
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
